import { isFeatureAvailable, useGetDevFeatures } from "@components/featureGuard/FeatureGuard"
import { UnderConfigurationStatusIcon } from "@hooks/useGetUnderConfigurationVessels"
import { UniSeaVesselIcon } from "@hooks/useGetUniSeaVessels"
import { CellSetterV1, TableBodyMapPage } from "@pages/HomePage/BodyContentLayout"
import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getListOfVessels, PlantInfoFE } from "@redux/features/listOfVessels/listOfVesselsSlice"
import { getOfflineVessels } from "@redux/features/offlineVessels/offlineVesselsSlice"
import { getUnderConfigurationVessels } from "@redux/features/underConfigurationVessels/underConfigurationVesselsSlice"
import { getUniSeaVessels } from "@redux/features/uniSeaVessels/uniSeaVesselsSlice"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { ColumnDef } from "@tanstack/react-table"
import { TableMapPage } from "@ui-lib/src"
import {
	VesselConnectionStatusIcon,
	VesselConnectionStatusIconProps,
} from "@utils/hooks/useGetVesselsConnectionStatusData"
import { PortalFeature } from "api"
import { ReactElement } from "react"
import { useTranslation } from "react-i18next"
import { getHardCodedVesselData, MapTableVesselData } from "./hard_coded_vessel_data"

export const fake_data_demo_vard: MapTableVesselData[] = [
	{
		vessel: "Vessel A",
		type: "CSV",
		capacity: { name: "Gross tonnage", value: 16500, unit: "" },
		charterer: "Client A",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel B",
		type: "CSV",
		capacity: { name: "Gross tonnage", value: 6055, unit: "" },
		charterer: "Client B",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel C",
		type: "PSV",
		capacity: { name: "Gross tonnage", value: 3959, unit: "" },
		charterer: "Client C",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel D",
		type: "PSV",
		capacity: { name: "Gross tonnage", value: 4469, unit: "" },
		charterer: "Client D",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel E",
		type: "CSV",
		capacity: { name: "Gross tonnage", value: 7300, unit: "" },
		charterer: "Client E",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel F",
		type: "Wellboat",
		capacity: { name: "Cargo hold", value: 6000, unit: "m³" },
		charterer: "Client F",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel G",
		type: "CSV",
		capacity: { name: "Gross tonnage", value: 8973, unit: "" },
		charterer: "Client G",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel H",
		type: "CLV",
		capacity: { name: "Gross tonnage", value: 8660, unit: "" },
		charterer: "Client H",
		filterGroup: undefined,
	},
	{ vessel: "Vessel I", type: "REV", charterer: "Client I", filterGroup: undefined },
]

export const fake_data_demo: MapTableVesselData[] = [
	{
		vessel: "Vessel A",
		type: "CSV",
		capacity: { name: "Gross tonnage", value: 16500, unit: "" },
		charterer: "Client A",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel B",
		type: "CSV",
		capacity: { name: "Gross tonnage", value: 6055, unit: "" },
		charterer: "Client B",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel C",
		type: "PSV",
		capacity: { name: "Gross tonnage", value: 3959, unit: "" },
		charterer: "Client C",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel D",
		type: "PSV",
		capacity: { name: "Gross tonnage", value: 4469, unit: "" },
		charterer: "Client D",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel E",
		type: "CSV",
		capacity: { name: "Gross tonnage", value: 7300, unit: "" },
		charterer: "Client E",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel F",
		type: "Wellboat",
		capacity: { name: "Cargo hold", value: 6000, unit: "m³" },
		charterer: "Client F",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel G",
		type: "CSV",
		capacity: { name: "Gross tonnage", value: 8973, unit: "" },
		charterer: "Client G",
		filterGroup: undefined,
	},
	{
		vessel: "Vessel H",
		type: "Tanker",
		capacity: { name: "Deadweight", value: 154036, unit: "t" },
		charterer: "Client H",
		filterGroup: undefined,
	},
]

type MapTableDofItem = {
	conditionLabel: string
	itemName: string
	type: string
	status: string
	charterer: string
	icons: (ReactElement<VesselConnectionStatusIconProps> | null)[]
	details: { paramLabel: "vessel"; paramValue: string }
}

type InferAccessorType<T extends keyof MapTableDofItem> = MapTableDofItem[T]

function useListOfPlantsTableColumns(typeColumnHeader?: string) {
	const { t } = useTranslation()
	const columns: ColumnDef<MapTableDofItem>[] =
		//  useMemo<ColumnDef<MapTableDofItem>[]>(
		// 	() =>
		[
			{
				accessorKey: "conditionLabel",
				header: () => <div className={`flex w-full justify-start text-left`}></div>,
				cell: (info) => {
					return CellSetterV1({
						content: {
							type: "condition-label",
							condition: info.getValue() as InferAccessorType<"conditionLabel">,
						},
						index: info.cell.id,
					})
				},
			},
			{
				accessorKey: "itemName",
				header: () => (
					<div className={`flex w-full justify-start text-left`}>{t("VESSEL")}</div>
				),
				cell: (info) => {
					return CellSetterV1({
						content: {
							type: "text",
							text: info.getValue() as InferAccessorType<"itemName">,
						},
						index: info.cell.id,
					})
				},
			},
			{
				accessorKey: "type",
				header: () => (
					<div className={`flex w-full justify-start text-left`}>
						{t(typeColumnHeader ?? "VESSEL TYPE")}
					</div>
				),
				cell: (info) => {
					return CellSetterV1({
						content: {
							type: "text",
							text: info.getValue() as InferAccessorType<"type">,
						},
						index: info.cell.id,
					})
				},
			},
			{
				accessorKey: "status",
				header: () => (
					<div className={`flex w-full justify-start text-left`}>{t("OPERATION")}</div>
				),
				cell: (info) => {
					return CellSetterV1({
						content: {
							type: "text",
							text: info.getValue() as InferAccessorType<"status">,
						},
						index: info.cell.id,
					})
				},
			},
			{
				accessorKey: "charterer",
				header: () => (
					<div className={`flex w-full justify-start text-left`}>{t("CHARTERER")}</div>
				),
				cell: (info) => {
					return CellSetterV1({
						content: {
							type: "text",
							text: info.getValue() as InferAccessorType<"charterer">,
						},
						index: info.cell.id,
					})
				},
			},
			{
				accessorKey: "icons",
				header: () => <div className={`flex w-full justify-start text-left`} />,
				cell: (info) => {
					return CellSetterV1({
						content: {
							type: "icons",
							icons: info.getValue() as InferAccessorType<"icons">,
						},
						index: info.cell.id,
					})
				},
			},
			{
				accessorKey: "details",
				header: () => <div className={`flex w-full justify-start text-left`} />,
				cell: (info) => {
					return CellSetterV1({
						content: {
							type: "url-param",
							...(info.getValue() as InferAccessorType<"details">),
						},
						index: info.cell.id,
					})
				},
			},
		]
	// 	[t]
	// )
	return columns
}

export function TableListOfPlants({
	customerName,
	vesselType,
}: {
	customerName: string
	vesselType?: string
}) {
	const { t } = useTranslation()
	const offlineVessels = useAppSelector(getOfflineVessels)
	const listOfVessels = useAppSelector(getListOfVessels)
	const uniSeaVessels = useAppSelector(getUniSeaVessels)
	const underConfigurationVessels = useAppSelector(getUnderConfigurationVessels)
	const userData = useAppSelector(getUserData)
	const devFeatures = useGetDevFeatures()
	const currentCompany = useAppSelector(getCurrentCompany) ?? ""

	const showDatalessPlants = isFeatureAvailable(
		undefined,
		PortalFeature.SHOW_DATALESS_PLANTS,
		currentCompany,
		devFeatures,
		userData.portalMode
	)
	const hardCodedVesselData =
		customerName === "Dipai Portal Demo"
			? fake_data_demo
			: customerName === "Vard Demo"
				? fake_data_demo_vard
				: getHardCodedVesselData(currentCompany)

	const listOfVesselTypesAndCapacities = new Set(
		listOfVessels.vessels
			.map((v) => [
				hardCodedVesselData.find((data) => data.vessel === v.name)?.capacity?.name,
				hardCodedVesselData.find((data) => data.vessel === v.name)?.type,
			])
			.filter(
				(type): type is [string, string] =>
					type[0] !== undefined &&
					type[0] !== "--" &&
					type[1] !== undefined &&
					type[1] !== "--"
			)
			.map((type) => type[0] + "//" + type[1])
	)

	const columns = useListOfPlantsTableColumns(
		vesselType === undefined
			? "VESSEL TYPE"
			: (Array.from(listOfVesselTypesAndCapacities)
					.find((t) => t.includes(vesselType))
					?.split("//")[0]
					?.toUpperCase() ?? "VESSEL TYPE")
	)

	const data: MapTableDofItem[] = (
		vesselType === undefined
			? listOfVessels.vessels
			: listOfVessels.vessels.filter(
					(vessel) =>
						(hardCodedVesselData.find((data) => data.vessel === vessel.name)?.type ??
							"--") === vesselType
				)
	)
		.map((vesselData, index) => {
			const vessel: PlantInfoFE = JSON.parse(JSON.stringify(vesselData))

			let statusLabel = ""
			// if (vessel.latestOperationMode?.deviationEvents !== undefined) {
			// 	const deviationEvents = vessel.latestOperationMode.deviationEvents

			// 	for (const event of deviationEvents) {
			// 		if (event.severity !== undefined) {
			// 			if (event.severity === "yellow" && statusLabel === "") {
			// 				statusLabel = "yelow"
			// 			}
			// 			if (event.severity === "red") {
			// 				statusLabel = "red"
			// 			}
			// 		}
			// 	}
			// }
			const offlineVesselData = offlineVessels?.find(
				(plantData) => plantData.id === vessel.plantId
			)
			const vesselUniSeaData = uniSeaVessels?.find(
				(plantData) => plantData.id === vessel.plantId
			)
			const vesselUnderConfigurationData = underConfigurationVessels?.find(
				(plantData) => plantData.id === vessel.plantId
			)

			const icons: (ReactElement<VesselConnectionStatusIconProps> | null)[] = []
			if (
				vesselUnderConfigurationData !== undefined ||
				offlineVesselData !== undefined ||
				vesselUniSeaData !== undefined
			) {
				statusLabel =
					vesselUnderConfigurationData !== undefined
						? "UnderConfiguration"
						: vesselUniSeaData !== undefined
							? "UniSea"
							: "yellow"

				icons.push(
					vesselUnderConfigurationData !== undefined ? (
						<UnderConfigurationStatusIcon colorTwString="text-slate-700" />
					) : vesselUniSeaData !== undefined ? (
						<UniSeaVesselIcon firstTimestamp={vesselUniSeaData.firstTimestamp} />
					) : offlineVesselData !== undefined ? (
						<VesselConnectionStatusIcon
							isAvailable={false}
							lastTimestamp={offlineVesselData.lastUpdateTimestamp}
							colorTwString="text-amber-700"
						/>
					) : null
				)
			}

			return {
				conditionLabel: statusLabel === "" ? "green" : statusLabel,
				itemName: `${vessel.name}${statusLabel === "yelow" ? " (offline)" : ""}`,
				status: `${
					vessel.latestOperationMode2 !== undefined
						? vessel.latestOperationMode2.opModeName
						: "--"
				}`,
				details: { paramLabel: "vessel" as const, paramValue: vessel.name },
				icons,
				type:
					vesselType === undefined
						? (hardCodedVesselData.find((data) => data.vessel === vessel.name)?.type ??
							"--")
						: hardCodedVesselData.find((data) => data.vessel === vessel.name)
									?.capacity !== undefined
							? `${hardCodedVesselData.find((data) => data.vessel === vessel.name)?.capacity?.value} ${hardCodedVesselData.find((data) => data.vessel === vessel.name)?.capacity?.unit}`
							: "--",
				charterer:
					hardCodedVesselData.find((data) => data.vessel === vessel.name)?.charterer ??
					"--",
			}
		})
		.filter((vesselData) => {
			const iconComponent = vesselData.icons?.[0]
			const vesselUnderConfigurationData = underConfigurationVessels?.find(
				(plantData) => plantData.name === vesselData.details.paramValue
			)
			const vesselUniSeaData = uniSeaVessels?.find(
				(plantData) => plantData.name === vesselData.details.paramValue
			)
			if (
				vesselUnderConfigurationData !== undefined ||
				vesselUniSeaData !== undefined ||
				showDatalessPlants === true ||
				iconComponent === undefined ||
				iconComponent === null
			) {
				return true
			}
			if (
				iconComponent.props.lastTimestamp === null ||
				iconComponent.props.lastTimestamp === undefined
			) {
				return false
			}
			return true
		})
		.sort((a, b) => (a.itemName > b.itemName ? 1 : a.itemName < b.itemName ? -1 : 0))
	// }, [listOfVessels, offlineVessels])

	return (
		<TableMapPage
			translationFunc={t}
			TableBody={TableBodyMapPage}
			columns={columns}
			columnsLayout={"grid-cols-[16px_120px_120px_120px_auto_30px_30px]"}
			data={data}
			itemsPerPage={10}
			loading={!listOfVessels.loaded}
			showGlobalFilter={true}
		/>
	)
}
